export type KeyEvaluation = 'correct' | 'present' | 'incorrect' | 'unused';

export interface KeyboardActionKey {
  /** The id passed to `onKey` when this key is pressed (e.g. `'enter'`). */
  key: string;
  /** Short visible label (e.g. `'Enter'`, `'Del'`). */
  label: string;
  /** Full accessible name; falls back to `label`. */
  ariaLabel?: string;
}

export interface KeyboardProps {
  /**
   * Character rows, top to bottom. Each entry is a single lowercase key id.
   * Defaults to a standard QWERTY layout.
   */
  rows?: readonly (readonly string[])[];
  /**
   * Per-key evaluated state. Any key not present renders as `'unused'`.
   * State is never conveyed by colour alone — every key also carries a
   * border treatment and a state phrase in its accessible name.
   */
  keyStates?: Readonly<Record<string, KeyEvaluation>>;
  /** Fired with the pressed key id — a character, or an action key's `key`. */
  onKey: (key: string) => void;
  disabled?: boolean;
  /** Leading key on the final row (default Enter). Pass `null` to omit. */
  enterKey?: KeyboardActionKey | null;
  /** Trailing key on the final row (default Backspace). Pass `null` to omit. */
  backspaceKey?: KeyboardActionKey | null;
  /** Accessible label for the keyboard region. */
  'aria-label'?: string;
  /** Human phrase per evaluated state, appended to each character key's name. */
  stateLabels?: Record<KeyEvaluation, string>;
}

const DEFAULT_ROWS: readonly (readonly string[])[] = [
  ['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'],
  ['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'],
  ['z', 'x', 'c', 'v', 'b', 'n', 'm'],
];

const DEFAULT_ENTER: KeyboardActionKey = { key: 'enter', label: 'Enter' };
const DEFAULT_BACKSPACE: KeyboardActionKey = {
  key: 'backspace',
  label: 'Del',
  ariaLabel: 'Delete',
};

const DEFAULT_STATE_LABELS: Record<KeyEvaluation, string> = {
  correct: 'correct position',
  present: 'in the word',
  incorrect: 'not in the word',
  unused: 'not tried yet',
};

// Solid fills from the canonical `correct` / `present` / `incorrect` design
// tokens (docs/UI_GUIDELINES.md §2, §8). Contrast of `textOnDark` on each is
// asserted in `packages/design-tokens/tests/tokens.test.ts`.
const STATE_CLASS: Record<KeyEvaluation, string> = {
  correct: 'border-correct bg-correct text-textOnDark',
  present: 'border-present bg-present text-textOnDark',
  incorrect: 'border-incorrect bg-incorrect text-textOnDark',
  unused: 'border-border bg-surface text-textPrimary hover:bg-surfaceElevated',
};

/**
 * The one on-screen keyboard for the whole platform (docs/UI_GUIDELINES.md
 * §8). Every game that needs a soft keyboard renders this — individual game
 * screens must not invent their own key dimensions, spacing, or states. All
 * geometry comes from `packages/design-tokens` `keyboard` (via the
 * `.px-keyboard*` classes in `apps/web/src/styles/index.css`), so the layout
 * degrades gracefully from 320px up with no horizontal overflow and no
 * device-specific breakpoints.
 */
export function Keyboard({
  rows = DEFAULT_ROWS,
  keyStates,
  onKey,
  disabled = false,
  enterKey = DEFAULT_ENTER,
  backspaceKey = DEFAULT_BACKSPACE,
  stateLabels = DEFAULT_STATE_LABELS,
  ...rest
}: KeyboardProps) {
  const lastRowIndex = rows.length - 1;

  return (
    <div
      className="px-keyboard"
      role="group"
      aria-label={rest['aria-label'] ?? 'On-screen keyboard'}
    >
      {rows.map((row, rowIndex) => {
        const isLastRow = rowIndex === lastRowIndex;
        return (
          <div key={rowIndex} className="px-keyboard__row">
            {isLastRow && enterKey && (
              <ActionButton spec={enterKey} disabled={disabled} onKey={onKey} />
            )}
            {row.map((char) => {
              const state = keyStates?.[char] ?? 'unused';
              return (
                <button
                  key={char}
                  type="button"
                  disabled={disabled}
                  aria-label={`${char.toUpperCase()}, ${stateLabels[state]}`}
                  onClick={() => onKey(char)}
                  className={`px-keyboard__key interactive-lift transition-colors duration-fast ${STATE_CLASS[state]}`}
                >
                  {char}
                </button>
              );
            })}
            {isLastRow && backspaceKey && (
              <ActionButton spec={backspaceKey} disabled={disabled} onKey={onKey} />
            )}
          </div>
        );
      })}
    </div>
  );
}

function ActionButton({
  spec,
  disabled,
  onKey,
}: {
  spec: KeyboardActionKey;
  disabled: boolean;
  onKey: (key: string) => void;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={spec.ariaLabel ?? spec.label}
      onClick={() => onKey(spec.key)}
      className="px-keyboard__key px-keyboard__key--action interactive-lift border-borderStrong bg-surfaceAlt text-textPrimary transition-colors duration-fast hover:bg-surfaceElevated"
    >
      {spec.label}
    </button>
  );
}
